import { makeAutoObservable, reaction, when, type IReactionDisposer } from 'mobx'
import { Call } from '@twilio/voice-sdk'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import { CallApi, type IResponseCallQueueParticipant } from 'entities/Call'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { type Contact } from 'entities/Contacts/model/Contact'
import { type Phone } from 'entities/Phone/model/Phone'
import { PowerDialer } from 'entities/PowerDialer/model/PowerDialer'
import { PowerDialerApi } from 'entities/PowerDialer/api/powerDialer'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { IntegrationKey } from 'entities/Integrations'
import type { ICallV1BroadcastMessage } from 'widgets/CallPopUp'
import { CallTeam } from 'widgets/CallPopUp/model/CallTeam'
import { getPowerDialerSession as getPowerDialerSessionV2 } from 'widgets/CallPopUp/store/powerDialer/powerDialerSessionStore'

export class CallPopUpGlobalStore {
  private _itemsTeamsMap: Map<number, CallTeam> = new Map()
  private _disposeReactionSignal: IReactionDisposer | null = null
  private _disposeReactionStatus: IReactionDisposer | null = null
  private _signalTakeCall = false
  private _hasActiveV1Call = false
  private _showNotesAfterCalling = false
  private _showNotesAfterVoicemailDrop = false
  private _resetCallingId: number | null = null
  private _resetCallingItem: CallTeam | null = null

  constructor(private _entities: IEntitiesStore) {
    this.reactionSignal()
    this.reactionStatus()
    this.whenLoggedInInitCallQueue()
    const chanel = new BroadcastChannel('v1-call-broadcast')

    chanel.onmessage = this.handleBroadcastMessage

    this._initCachedPowerDialerV2()

    makeAutoObservable(this)
  }

  get entities() {
    return this._entities
  }

  get callStore() {
    return this._entities.callStore
  }

  private _initCachedPowerDialerV2 = async () => {
    const powerDialerId = await getPowerDialerSessionV2()
    if (!powerDialerId) return

    const item = await this.openPowerDialer(powerDialerId)

    item?.store.callPopUpPowerDialerStoreV2.setPause(true)
  }

  reactionSignal = () => {
    this._disposeReactionSignal?.()
    this._disposeReactionSignal = reaction(
      () => this.callStore.signal,
      (signal) => {
        if (!signal) return
        if (signal.name === 'incoming' || signal.name === 'outgoing') {
          this.handleSignalTakeCall(false)

          const team = this.callStore.contactFrom
          const contact = this.callStore.contactTo
          const number = this._entities.numbersStore.getItem(team?.numberId)

          if (!team) return
          if (!contact) return
          if (!number) return

          const item = this.addDefaultItem(team, contact)

          if (item) {
            this.items.forEach((itemTeam) => {
              if (item.id === itemTeam.id) {
                itemTeam.setLocalStatusCall(this.callStore.status)
              } else {
                itemTeam.setLocalStatusCall(Call.State.Closed)
                itemTeam.callPopUpCallInsightCardsCallTypeStore?.setModalStatus(false)
              }
            })

            item.setStatusCall(this.callStore.status)
          }
        }
      }
    )
  }

  reactionStatus = () => {
    this._disposeReactionStatus?.()
    this._disposeReactionStatus = reaction(
      () => this.callStore.status,
      (value) => {
        if (!value) return

        this.items.forEach((item) => {
          item.setStatusCall(value)
        })

        const team = this.callStore.contactFrom
        const item = this.getItem(team?.id)
        if (item) {
          this.items.forEach((itemTeam) => {
            if (item.id === itemTeam.id) {
              itemTeam.setLocalStatusCall(value)
            } else {
              itemTeam.setLocalStatusCall(Call.State.Closed)
              itemTeam.callPopUpCallInsightCardsCallTypeStore?.setModalStatus(false)
            }
            itemTeam.callPopUpCallInsightCardsCallTypeStore?.setCallSid(
              this.callStore.connect?.parameters.CallSid || ''
            )
          })

          if (value === Call.State.Closed && this.callStore.contactFrom) {
            this.updateCallTeamView(this.callStore.contactFrom.id)
          }
        }
      }
    )
  }

  whenLoggedInInitCallQueue = () => {
    when(
      () => this._entities.authStore.isLoggedIn,
      () => this.initActiveCallQueue()
    )
  }

  handleSignalTakeCall = (status: boolean) => {
    this._signalTakeCall = status
  }

  handleBroadcastMessage = (event: MessageEvent<ICallV1BroadcastMessage>): void => {
    if (event.data.message === 'call_start') {
      this.setActiveV1Call(true)
    }

    if (event.data.message === 'call_end') {
      this.setActiveV1Call(false)
    }
  }

  setActiveV1Call = (activeV1Call: boolean): void => {
    this._hasActiveV1Call = activeV1Call
  }

  addDefaultItem = (team: Inbox, contact: Contact) => {
    let item = this.getItem(team.id)

    if (!item) {
      const number = this._entities.numbersStore.getItem(team.numberId)

      if (!number) return

      item = this.addItem(
        new CallTeam(
          {
            id: team.id,
            name: team.name,
            number: number.formatted_number,
          },
          this._entities
        )
      )
    }

    if (!item) return

    item.setContactFrom(team)
    item.setContactTo(contact)
    item.setView('default')
    item.setStatusCall(this.callStore.status)

    return item
  }

  private _addPowerDialerCallTeamItem = async (
    team: Inbox,
    number: Phone | null,
    powerDialer: PowerDialer
  ) => {
    let item = this.getItem(team.id)

    if (!item) {
      item = new CallTeam(
        {
          id: team.id,
          name: team.name,
          number: number?.formatted_number ?? '',
        },
        this._entities
      )
    }

    item.setView('default')
    item.setContactFrom(team)

    await item.setPowerDialerInitData({
      team,
      number,
      powerDialer,
      removePowerDialer: (teamId: number) => this.updateCallTeamView(teamId),
    })

    return this.addItem(item)
  }

  openPowerDialer = async (powerDialerId: number) => {
    try {
      const {
        data: { data: powerDialerResponse },
      } = await PowerDialerApi.getPowerDialerWithUpdatedContactsCount(powerDialerId)
      const powerDialer = new PowerDialer(powerDialerResponse, this._entities)

      const number = powerDialer.number
      const teamId = number?.numberable?.id || powerDialer.sendFrom.options.team_id
      const team = teamId ? await this._entities.inboxesStore.getById(teamId) : null

      if (!(team instanceof Inbox)) {
        showToast({
          type: 'error',
          title: 'Inbox processing error.',
        })
        return
      }

      return this._addPowerDialerCallTeamItem(team, number, powerDialer)
    } catch (error) {
      logger.error(error)
    }
  }

  initActiveCallQueue = async () => {
    try {
      const { data } = await CallApi.getCallQueue()

      data.forEach((queueItem) => {
        this._entities.inboxesStore.channelsInboxes.subscribeChannel(queueItem.team.id)

        const sortedParticipants = [...queueItem.participants].sort(
          (first, second) => (first.call.position ?? 0) - (second.call.position ?? 0)
        )

        sortedParticipants.forEach((participant) => {
          this.addQueueItem({
            team: queueItem.team,
            contact: participant.contact,
            call: participant.call,
          })
        })
      })
    } catch (error) {
      logger.error(error)
    }
  }

  addQueueItem = (itemResponse: IResponseCallQueueParticipant) => {
    const item = this.getItem(itemResponse.team.id)

    if (item) {
      if (!item.hasItemQueue) {
        item.setItemQueue(itemResponse)
      }

      if (itemResponse.contact) {
        item.addItemQueue({
          contact: itemResponse.contact,
          call: itemResponse.call,
        })
      }
    } else {
      const item = new CallTeam(
        {
          ...itemResponse.team,
        },
        this._entities
      )

      item.setView('queue')
      item.setItemQueue(itemResponse)

      if (itemResponse.contact) {
        item.addItemQueue({
          contact: itemResponse.contact,
          call: itemResponse.call,
        })
      }

      this.addItem(item)
    }
  }

  addItem = (item: CallTeam) => {
    this._itemsTeamsMap.set(item.id, item)

    return item
  }

  getItem = (id?: number) => {
    if (!id) return

    return this._itemsTeamsMap.get(id)
  }

  updateCallTeamView = (teamId: number) => {
    const item = this.getItem(teamId)
    if (!item || !teamId) return

    const isVoicemailDropCompleted = this.callStore.voicemailDropCompleted
    if (isVoicemailDropCompleted) {
      this.callStore.setVoicemailDropCompleted(false)
    }

    if (item.isCallTypesModal && !isVoicemailDropCompleted) {
      item.callPopUpCallInsightCardsCallTypeStore?.setCallBack({
        callBack: () => {
          item.resetAll()
          this._itemsTeamsMap.delete(teamId)
          item.callPopUpInsightCardsStore?.callNoteStore?.handleMessageSend?.()
        },
      })

      return
    }

    if (item.isPowerDialerRunning) return
    if (item.hasItemsQueue) {
      item.setView('queue')

      return
    }

    this._resetCallingItem = item
    this._resetCallingId = teamId

    if (
      !this._showNotesAfterVoicemailDrop &&
      (isVoicemailDropCompleted || !this._showNotesAfterCalling)
    ) {
      if (item.callPopUpCallInsightCardsCallTypeStore && !isVoicemailDropCompleted) {
        item.callPopUpCallInsightCardsCallTypeStore.setCallBack({
          callBack: () => {
            this.resetCalling()
          },
        })
      } else {
        this.resetCalling()
      }
    }
  }

  resetCalling = () => {
    if (this._resetCallingItem) this._resetCallingItem.resetAll()
    if (this._resetCallingId) this._itemsTeamsMap.delete(this._resetCallingId)
    this._resetCallingItem = null
    this._resetCallingId = null
  }

  setShowNotesAfterCallingGlobal = (state: boolean) => {
    this._showNotesAfterCalling = state
  }

  setShowNotesAfterVoicemailDrop = (state: boolean) => {
    this._showNotesAfterVoicemailDrop = state
  }

  deleteItem = (itemResponse: IResponseCallQueueParticipant) => {
    const item = this.getItem(itemResponse.team.id)

    if (!item) return

    if (itemResponse.contact) {
      item.store.callPopUpQueueStore.deleteItem(itemResponse.contact.id)
    }

    const isDesignV2 = this._entities.userSettingsStore.isSalesmsgDesignV2
    const isNotDevice = isDesignV2 ? true : !this.callStore.device
    const isNotSignalTakeCall = isDesignV2 ? !this._signalTakeCall : true
    const isNotItems = !item.store.callPopUpQueueStore.items.length
    const isViewQueue = item.store.view === 'queue'

    if (isNotSignalTakeCall && isNotDevice && isNotItems && isViewQueue) {
      this._itemsTeamsMap.delete(itemResponse.team.id)
    }
  }

  get items() {
    return Array.from(this._itemsTeamsMap.values())
  }

  get itemsSort() {
    const sortCallState = {
      [Call.State.Connecting]: 0,
      [Call.State.Open]: 0,
      [Call.State.Pending]: 0,
      [Call.State.Reconnecting]: 0,
      [Call.State.Ringing]: 0,
      [Call.State.Closed]: 1,
    }
    const sortTypeView = {
      default: 0,
      queue: 1,
    }

    return this.items
      .sort((a, b) => {
        const aValue = sortTypeView[a.view]
        const bValue = sortTypeView[b.view]

        return aValue - bValue
      })
      .sort((a, b) => {
        const aValue = sortCallState[a.status]
        const bValue = sortCallState[b.status]

        return aValue - bValue
      })
  }

  get hasActiveV1Call() {
    return this._hasActiveV1Call
  }

  get hasPowerDialerV2() {
    return this.items.some((item) => item.isPowerDialerRunning)
  }

  get isExternalSourcePipedrive() {
    const params = new URLSearchParams(window.location.search)
    const externalSource = params.get('external_source')
    return externalSource === IntegrationKey.pipedrive
  }
}
